"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { getPublishedCaseStudies } from "@/data/caseStudies";
import "./parentStoryBubble.css";

const STORAGE_KEY = "tsc-parent-story-hidden";
const CYCLE_MS = 7000;
const RING_RADIUS = 46;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

type NetworkInfo = { saveData?: boolean };

type Props = {
  /** Also show the bubble on /services pages. Homepage only by default. */
  includeServicePages?: boolean;
};

function shouldShow(pathname: string | null, includeServicePages: boolean) {
  if (!pathname) return false;
  if (pathname === "/") return true;
  if (includeServicePages && pathname.startsWith("/services")) return true;
  return false;
}

function scheduleIdle(callback: () => void, timeout: number) {
  const idleWindow = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };

  if (idleWindow.requestIdleCallback) {
    const id = idleWindow.requestIdleCallback(callback, { timeout });
    return () => idleWindow.cancelIdleCallback?.(id);
  }

  const timer = window.setTimeout(callback, Math.min(timeout, 400));
  return () => window.clearTimeout(timer);
}

export default function ParentStoryBubble({ includeServicePages = false }: Props) {
  const pathname = usePathname();
  const stories = getPublishedCaseStudies();
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [index, setIndex] = useState(0);
  const [labelOn, setLabelOn] = useState(false);
  const [posterOnly, setPosterOnly] = useState(false);
  const [preloadRest, setPreloadRest] = useState(false);
  const [reduced, setReduced] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const ringRef = useRef<SVGCircleElement | null>(null);

  const visible = shouldShow(pathname, includeServicePages) && stories.length > 0;

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(media.matches);
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!visible) return;

    let hidden = false;
    try {
      hidden = sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      hidden = false;
    }
    if (hidden) {
      setDismissed(true);
      return;
    }

    const connection = (navigator as Navigator & { connection?: NetworkInfo }).connection;
    if (connection?.saveData) setPosterOnly(true);

    return scheduleIdle(() => setReady(true), 1200);
  }, [visible]);

  useEffect(() => {
    if (!ready || posterOnly) return;
    return scheduleIdle(() => setPreloadRest(true), 2000);
  }, [ready, posterOnly]);

  useEffect(() => {
    if (!ready) return;
    const show = window.setTimeout(() => setLabelOn(true), 2000);
    const hide = window.setTimeout(() => setLabelOn(false), 9000);
    return () => {
      window.clearTimeout(show);
      window.clearTimeout(hide);
    };
  }, [ready]);

  useEffect(() => {
    if (!ready || reduced || stories.length < 2) return;

    let frame = 0;
    let start = performance.now();
    let hiddenAt = 0;

    const loop = (now: number) => {
      if (document.hidden) {
        if (!hiddenAt) hiddenAt = now;
        frame = window.requestAnimationFrame(loop);
        return;
      }
      if (hiddenAt) {
        start += now - hiddenAt;
        hiddenAt = 0;
      }

      const elapsed = now - start;
      const progress = Math.min(elapsed / CYCLE_MS, 1);
      if (ringRef.current) {
        ringRef.current.style.strokeDashoffset = String(RING_CIRC * (1 - progress));
      }
      if (elapsed >= CYCLE_MS) {
        start = now;
        setIndex((current) => (current + 1) % stories.length);
      }
      frame = window.requestAnimationFrame(loop);
    };

    frame = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(frame);
  }, [ready, reduced, stories.length]);

  useEffect(() => {
    if (!ready) return;

    const syncPlayback = () => {
      videoRefs.current.forEach((video, videoIndex) => {
        if (!video) return;
        const active = videoIndex === index && !posterOnly && !document.hidden;
        if (active) {
          const pending = video.play();
          if (pending) pending.catch(() => setPosterOnly(true));
        } else {
          video.pause();
        }
      });
    };

    syncPlayback();
    document.addEventListener("visibilitychange", syncPlayback);
    return () => document.removeEventListener("visibilitychange", syncPlayback);
  }, [ready, index, posterOnly, preloadRest]);

  if (!visible || dismissed || !ready) return null;

  const story = stories[index];

  const hide = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* private browsing */
    }
    setDismissed(true);
  };

  return (
    <div className="psb">
      <div className="psb-motion">
        <button type="button" className="psb-close" aria-label="Hide parent stories" onClick={hide}>
          <span aria-hidden="true">×</span>
        </button>
        <Link
          href={`/case-studies/${story.slug}`}
          className="psb-link"
          aria-label={
            story.childFirstName
              ? `Watch ${story.childFirstName}'s story${story.service ? `: ${story.service}` : ""}`
              : "Watch a parent story"
          }
        >
          <span className="psb-pulse" aria-hidden="true" />
          <span className="psb-spin" aria-hidden="true" />
          <span className="psb-face">
            {stories.map((item, itemIndex) => {
              const active = itemIndex === index;
              const loadClip = !posterOnly && (itemIndex === 0 || preloadRest);
              return loadClip ? (
                <video
                  key={item.slug}
                  ref={(node) => {
                    videoRefs.current[itemIndex] = node;
                  }}
                  className={`psb-slide${active ? " is-active" : ""}`}
                  src={item.bubbleClip}
                  poster={item.poster}
                  muted
                  loop
                  playsInline
                  preload={itemIndex === 0 ? "metadata" : "none"}
                  aria-hidden="true"
                />
              ) : (
                <img
                  key={item.slug}
                  src={item.poster}
                  alt=""
                  width={92}
                  height={92}
                  className={`psb-slide${active ? " is-active" : ""}`}
                />
              );
            })}
            <span className="psb-play" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="#fff">
                <path d="M8 5.14v13.72a1 1 0 0 0 1.53.85l10.2-6.86a1 1 0 0 0 0-1.7L9.53 4.29A1 1 0 0 0 8 5.14z" />
              </svg>
            </span>
          </span>
          <span className="psb-pill">Parent story</span>
          <svg className="psb-progress" viewBox="0 0 100 100" aria-hidden="true">
            <circle
              ref={ringRef}
              cx="50"
              cy="50"
              r={RING_RADIUS}
              strokeDasharray={RING_CIRC}
              strokeDashoffset={RING_CIRC}
            />
          </svg>
        </Link>
        <p className={`psb-label${labelOn ? " is-on" : ""}`} aria-hidden="true">
          <strong>
            {story.childFirstName ? `Watch ${story.childFirstName}'s story` : "Watch a parent story"}
          </strong>
          {story.service ? `${story.service} · ` : ""}tap to see the full video
        </p>
      </div>
    </div>
  );
}
